import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  classifyPanLookupError,
  isValidPanFormat,
  lookupPanName,
} from "../../../services/panNameLookup";
import { useHydrationCount } from "../hooks/HydrationContext";
import { useUnlockedFields } from "../hooks/UnlockedFieldsContext";

const PAN_FIELD = "pan_number";
const LEGAL_NAME_FIELD = "vendor_legal_name";
/** Shown under the legal-name field while it is unlocked for manual entry. */
export const UNLOCKED_HELPER_TEXT =
  "Couldn't be verified automatically just now — enter it exactly as it appears on the PAN card.";
const VENDOR_TYPE_FIELD = "vendor_type";
const LOOKUP_DEBOUNCE_MS = 400;

function isPanDriven(vendorType: string | undefined): boolean {
  return vendorType === "GST" || vendorType === "Non-GST";
}

export function PanNameAutofill() {
  const { control, setValue, setError, clearErrors } = useFormContext();
  const rawPan = useWatch({ control, name: PAN_FIELD }) as string | undefined;
  const vendorType = useWatch({ control, name: VENDOR_TYPE_FIELD }) as
    | string
    | undefined;
  const inFlightForRef = useRef<string | null>(null);
  const lastResolvedRef = useRef<string | null>(null);
  const previousVendorTypeRef = useRef<string | undefined>(vendorType);
  // pan_number is `hidden` under International — SchemaRenderer clears it,
  // Controller's defaultValue causes a visual/state desync on remount. Ref
  // holds the last known-valid PAN so we can restore it into RHF on flip
  // back and re-sync visible + state (also re-triggers the lookup).
  const lastValidPanRef = useRef<string | null>(null);
  const previousRawPanRef = useRef<string | undefined>(rawPan);
  const hydrationCount = useHydrationCount();
  const { setFieldUnlocked } = useUnlockedFields();
  const lastSeenHydrationRef = useRef<number>(hydrationCount);

  useEffect(() => {
    const prevRawPan = previousRawPanRef.current;
    previousRawPanRef.current = rawPan;
    if (typeof rawPan !== "string") return;
    const upper = rawPan.toUpperCase();
    if (upper !== rawPan) {
      setValue(PAN_FIELD, upper, { shouldValidate: false, shouldDirty: true });
      return;
    }
    if (isValidPanFormat(upper)) {
      lastValidPanRef.current = upper;
    } else if (
      upper === "" &&
      typeof prevRawPan === "string" &&
      prevRawPan !== "" &&
      isPanDriven(vendorType)
    ) {
      // A true non-empty → empty rawPan change while PAN-driven is a
      // deliberate vendor clear (hidden-clear sets pan to undefined, not
      // "", and only under International). prevRawPan gate stops
      // vendorType-only re-runs from nulling on pre-existing empty.
      lastValidPanRef.current = null;
    }
  }, [rawPan, vendorType, setValue]);

  useEffect(() => {
    const prev = previousVendorTypeRef.current;
    previousVendorTypeRef.current = vendorType;
    // Swallow ticks landing with a usePersistedFormData restore — the
    // vendor_type change here is the draft's saved value, not a user flip,
    // and wiping vendor_legal_name would silently drop the restored name
    // (fatal for International, where no PAN lookup can refill it).
    if (hydrationCount !== lastSeenHydrationRef.current) {
      lastSeenHydrationRef.current = hydrationCount;
      return;
    }
    // Skip transitions from the buildDefaultValues seed (undefined or "")
    // to any real value — those are the initial async form.reset lands.
    if (prev === undefined || prev === "") return;
    // Only clear + restore across the International boundary. Guards
    // GST ↔ Non-GST toggles from a blank-and-refetch flicker for the same PAN.
    if (isPanDriven(prev) === isPanDriven(vendorType)) return;
    setValue(LEGAL_NAME_FIELD, "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    lastResolvedRef.current = null;
    inFlightForRef.current = null;
    if (
      isPanDriven(vendorType) &&
      lastValidPanRef.current &&
      (rawPan ?? "").trim() === ""
    ) {
      setValue(PAN_FIELD, lastValidPanRef.current, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  }, [vendorType, rawPan, hydrationCount, setValue]);

  useEffect(() => {
    if (!isPanDriven(vendorType)) return;
    const normalized = (rawPan ?? "").trim().toUpperCase();

    if (!isValidPanFormat(normalized)) {
      lastResolvedRef.current = null;
      inFlightForRef.current = null;
      setValue(LEGAL_NAME_FIELD, "", { shouldValidate: false, shouldDirty: true });
      clearErrors(PAN_FIELD);
      setFieldUnlocked(LEGAL_NAME_FIELD, false);
      return;
    }
    if (lastResolvedRef.current === normalized) return;
    if (inFlightForRef.current === normalized) return;

    const timer = setTimeout(() => {
      inFlightForRef.current = normalized;
      void (async () => {
        try {
          const result = await lookupPanName(normalized);
          if (inFlightForRef.current !== normalized) return;
          lastResolvedRef.current = normalized;
          if (result.valid && result.legalName) {
            setValue(LEGAL_NAME_FIELD, result.legalName, {
              shouldValidate: true,
              shouldDirty: true,
            });
            clearErrors(PAN_FIELD);
            setFieldUnlocked(LEGAL_NAME_FIELD, false);
          } else {
            setError(PAN_FIELD, {
              type: "pan-lookup",
              message: "PAN not found. Please re-check the number.",
            });
          }
        } catch (err) {
          if (inFlightForRef.current !== normalized) return;
          const info = classifyPanLookupError(err);
          // Registry can't answer → let the vendor type the name (server re-checks it at /validate); a malformed PAN stays locked.
          if (info.kind !== "malformed") {
            setFieldUnlocked(LEGAL_NAME_FIELD, true, { helperText: UNLOCKED_HELPER_TEXT });
          }
          if (info.kind === "unavailable") return;
          setError(PAN_FIELD, { type: "pan-lookup", message: info.message });
        } finally {
          if (inFlightForRef.current === normalized) {
            inFlightForRef.current = null;
          }
        }
      })();
    }, LOOKUP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [rawPan, vendorType, setValue, setError, clearErrors, setFieldUnlocked]);

  return null;
}
